import { Trans } from '@lingui/react/macro';

import Accordion from '@/design_system/Accordion';
import { Separator } from '@/design_system/Menu/Menu';
import PhotoCard from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { getProductPictureFromArticleColor, useArticleName } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { ArticleInformationFormDrawer } from '@/routes/Requests/Request/Article/ArticleInformationFormDrawer';
import { ArticlePrice } from '@/routes/Requests/Request/Article/ArticlePrice';
import { ActionsSummary } from '@/routes/Requests/Request/Article/components/ArticleInfo/ActionsSummary';
import { ArticleAuthenticity } from '@/routes/Requests/Request/Article/components/ArticleInfo/ArticleAuthenticity';
import { ArticleDetails } from '@/routes/Requests/Request/Article/components/ArticleInfo/ArticleDetails';
import { ArticleWarranty } from '@/routes/Requests/Request/Article/components/ArticleInfo/ArticleWarranty';
import { RequestsHistory } from '@/routes/Requests/Request/Article/components/ArticleInfo/RequestsHistory';
import useViewPort from '@/utils/useViewport';

import { useNewArticleInfoSidePanelSections } from './useNewArticleInfoSidePanelSections';

export const NewArticleInfoSidePanelTop = () => {
  const { isMobile } = useViewPort();
  const { article } = useArticleContext();
  const name = useArticleName({ article });
  const { showPrices, allowDiscount, showProductName } = useNewArticleInfoSidePanelSections();

  return (
    <Stack gap="0.5rem">
      {showProductName && (
        <Stack padding={isMobile ? '1.2rem 0 0.8rem 1.5rem' : undefined} row alignItems="center">
          <Title as="h1" maxLines={!isMobile ? 2 : undefined} title={name}>
            {name}
          </Title>
        </Stack>
      )}
      {showPrices && !isMobile && <ArticlePrice allowDiscount={allowDiscount} hideArticleName />}
    </Stack>
  );
};

export const NewArticleInfoSidePanelBody = () => {
  const { isMobile } = useViewPort();
  const { article } = useArticleContext();
  const { showProductPicture, showHistory, showWarranty, showAuthenticity, showInformation } =
    useNewArticleInfoSidePanelSections();

  const productPicture = getProductPictureFromArticleColor({ article });

  return (
    <Stack gap="0.5rem">
      <Stack padding={isMobile ? '0 0.75rem' : 0} gap={'0.5rem'}>
        {showProductPicture && !!productPicture && (
          <>
            <Accordion
              trigger={
                <Paragraph weight="medium">
                  <Trans id="requests.new.articles.drawer.history.product-picture.title">
                    Product picture
                  </Trans>
                </Paragraph>
              }
              openByDefault
            >
              <PhotoCard
                name={article.product!.name}
                alt={article.product!.name}
                url={productPicture}
                size="xxlarge"
              />
            </Accordion>
          </>
        )}
        {showInformation && (
          <Accordion
            trigger={
              <Paragraph weight="medium">
                <Trans id="article.information.title">Information</Trans>
              </Paragraph>
            }
          >
            <ArticleDetails />
          </Accordion>
        )}
        <ArticleInformationFormDrawer />
        {showWarranty && (
          <>
            <Separator />

            <Accordion
              openByDefault={false}
              trigger={
                <Paragraph weight="medium">
                  <Trans id="article.warranty.title">Warranty</Trans>
                </Paragraph>
              }
            >
              <ArticleWarranty />
            </Accordion>
          </>
        )}
        {showAuthenticity && (
          <>
            <Separator />

            <Accordion
              openByDefault={false}
              trigger={
                <Paragraph weight="medium">
                  <Trans id="article.authenticity.title">Authenticity</Trans>
                </Paragraph>
              }
            >
              <ArticleAuthenticity />
            </Accordion>
          </>
        )}
        {showHistory && (
          <>
            <Separator />
            <Accordion
              openByDefault
              trigger={
                <Stack row gap="0.5rem" alignItems="center">
                  <Paragraph weight="medium">
                    <Trans id="article.actions-summary.title">Actions summary</Trans>
                  </Paragraph>
                </Stack>
              }
            >
              <ActionsSummary />
            </Accordion>

            <Separator />

            <Accordion
              openByDefault
              trigger={
                <Stack row gap="0.5rem" alignItems="center">
                  <Paragraph weight="medium">
                    <Trans id="article.requests-history.title">Requests history</Trans>
                  </Paragraph>
                </Stack>
              }
            >
              <RequestsHistory />
            </Accordion>
          </>
        )}
      </Stack>
    </Stack>
  );
};
